import { get } from "../ApiClient"
import { DefaultApiResponse } from "../models/response/DefaultApiResponse"
import EndpointProps from "interface/EndpointProps"

export interface WorkspaceTokenData {
    /** Full URL to redirect the user to (e.g. https://app.zeroneanalytiqs.com). */
    workspaceUrl: string
    /**
     * Raw workspace JWT — appended as `?token=` when redirecting so the analytics
     * platform can bootstrap a session even in dev where cookie sharing is impossible.
     */
    token: string
    /**
     * User's first name (decrypted by insight-hub-server) — passed to the analytics
     * platform via URL param so the welcome banner can show the name immediately
     * without a separate /users/me call.
     */
    displayName?: string
    /**
     * Resolved Firebase profile photo URL — passed to the analytics platform so
     * it can display the avatar without needing Firebase access itself.
     */
    avatarUrl?: string
}

export type WorkspaceTokenError =
    | 'subscription_required'   // 403 — user has no active paid subscription
    | 'org_inactive'            // 403 — org owner's subscription has lapsed
    | 'email_not_verified'      // 403 — user hasn't verified their email address
    | 'must_change_password'    // 403 — account still has a system-generated password (e.g. Pilot admin-created accounts)
    | 'pilot_expired'           // 403 — Pilot's 4-week access window has ended
    | 'account_suspended'       // 403 — account is suspended, revoked or deleted
    | 'unauthorized'            // 401 — user is not authenticated
    | 'server_error'            // 5xx — unexpected failure

export interface WorkspaceTokenResult {
    success: boolean
    data?: WorkspaceTokenData
    error?: WorkspaceTokenError
    /** paymentStatus from the subscription — populated on subscription_required */
    paymentStatus?: string
    planName?: string
}

/**
 * Requests a short-lived workspace JWT from the corporate backend.
 *
 * The server:
 *  - Verifies the user's JWT (session must already be active).
 *  - Checks the Stripe subscription for `paymentStatus === 'paid'`.
 *  - Returns { workspaceUrl, token } on success.
 *  - Returns 403 with message containing 'subscription' when not paid.
 *  - Returns 401 when the session is invalid / expired.
 *
 * NOTE on response unwrapping:
 * `handleFetchResponse` sets `data = jsonResponse` (the full API envelope body).
 * So the generic type must be `DefaultApiResponse<WorkspaceTokenData>` and the
 * dataExtractor must read `.data` to get the actual payload — the same pattern
 * every other GET endpoint uses.
 *
 * @param param0 - Standard endpoint props containing the logging tag.
 * @returns WorkspaceTokenResult
 */
export async function fetchWorkspaceToken({
    tag,
}: EndpointProps): Promise<WorkspaceTokenResult> {
    try {
        // Call get() directly so error responses retain their `data` field,
        // which fetchApiData strips. We need data.reason to distinguish
        // 'subscription_required' from 'org_inactive'.
        // handleFetchResponse sets data = full JSON envelope body, so response.data
        // is DefaultApiResponse<WorkspaceTokenData>. The actual payload is response.data?.data.
        const { response } = await get<DefaultApiResponse<WorkspaceTokenData>>({
            tag,
            url: '/workspace/token',
        })

        // ── Success ───────────────────────────────────────────────────────────
        if (response.status === 'success' && response.data?.data) {
            return { success: true, data: response.data.data }
        }

        // ── Error — read reason from response data, fall back to message ──────
        // On error responses handleFetchResponse sets data = jsonResponse.data (the server's
        // data field directly, not the full envelope), so we read it flat — not .data.data.
        const errorData = response.data as { reason?: string; paymentStatus?: string; planName?: string } | null
        const reason = errorData?.reason
        const msg = `${response.message ?? ''} ${response.error ?? ''}`.toLowerCase()

        if (reason === 'email_not_verified') {
            return { success: false, error: 'email_not_verified' }
        }

        if (reason === 'must_change_password') {
            return { success: false, error: 'must_change_password' }
        }

        if (reason === 'pilot_expired') {
            return { success: false, error: 'pilot_expired' }
        }

        if (reason === 'account_suspended') {
            return { success: false, error: 'account_suspended' }
        }

        if (reason === 'org_subscription_inactive') {
            return { success: false, error: 'org_inactive' }
        }

        if (reason === 'subscription_required' || msg.includes('subscription') || msg.includes('paid')) {
            return {
                success: false,
                error: 'subscription_required',
                ...(errorData?.paymentStatus !== undefined && { paymentStatus: errorData.paymentStatus }),
                ...(errorData?.planName !== undefined && { planName: errorData.planName }),
            }
        }

        if (msg.includes('unauthorized') || msg.includes('missing') || msg.includes('invalid')) {
            return { success: false, error: 'unauthorized' }
        }

        return { success: false, error: 'server_error' }
    } catch {
        return { success: false, error: 'server_error' }
    }
}
